/* iceberg-ui */
import { Button, Flex, WizardPage } from '@auviknetworks/iceberg-ui';

/* MUI */
import { CheckCircle } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/* Shared */
import { SuccessfulIntegrationSetup } from 'application/Shared/Components/App/SuccessfulIntegrationSetup';
import { Notify } from 'application/Shared/Helpers';
import { ProviderType } from 'application/Shared/types/tenantProvider';
import axios from 'axios';

/* React */
import React, { useContext, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ChooseGroups } from './Steps/ChooseGroups';

/* Internal Components */
import { ChooseIntegration } from './Steps/ChooseIntegration';
import { ConfigureIntegration } from './Steps/ConfigureIntegration';
import { SearchingAppsStep } from './Steps/SearchingAppsStep';
import { Wizard, WizardContext } from './Wizard';

const footerStyles = {
    justifyContent: 'space-between',
    marginTop: 'auto',
    padding: '12px'
};

const FirstButton = () => {
    const { t } = useTranslation();
    const { toNextStep } = useContext(WizardContext);
    return <Button onClick={() => toNextStep()}>{t('Common.next')}</Button>;
};

const UnsubmitBackButton = ({ setUseIntegrationSubmitted }) => {
    const { t } = useTranslation();
    return (
        <Button
            onClick={() => setUseIntegrationSubmitted(false)}
            variant="secondary"
        >
            {t('Common.back')}
        </Button>
    );
};

const BackButton = () => {
    const { toPreviousStep } = useContext(WizardContext);
    const { t } = useTranslation();

    return (
        <Button onClick={toPreviousStep} variant="secondary">
            {t('Common.back')}
        </Button>
    );
};

const SubmitGroupsButton = ({
    groups,
    providerRoute,
    linkedTenantId,
    skipAnimation,
    onSuccess
}) => {
    const { t } = useTranslation();
    const { toNextStep } = useContext(WizardContext);
    const platformTenantId = linkedTenantId;

    const [isSubmitting, setIsSubmitting] = useState(false);

    const providerType =
        providerRoute === 'google'
            ? ProviderType.Google
            : ProviderType.Microsoft;

    const onClick = async () => {
        setIsSubmitting(true);
        // get all values from the groups object and make an api call
        // to submit the groups.
        const promises = Object.values(groups).map(async (group) => {
            const { exists, id, isOrgUnit } = group;

            /* params */
            const params = {
                excluded: true,
                id,
                isOrgUnit,
                platformTenantId
            };

            try {
                // This case is a failsafe. The frontend doesn't display the checkbox
                // if the group is already in the database for the tenant.
                if (exists) {
                    // submit the group
                    return await axios.put(
                        `/${providerRoute}/tenantGroup/${id}`,
                        params
                    );
                }
                // create the group
                return await axios.post(
                    `/${providerRoute}/tenantGroup`,
                    params
                );
            } catch (error) {
                return error.response;
            }
        });

        try {
            const responses = await Promise.all(promises);
            const success = responses.every(
                (response) => response.status < 400
            );
            if (success) {
                await axios.post(`/tenantProvider/quickscan`, {
                    platformTenantId,
                    providerType
                });

                if (skipAnimation && onSuccess) {
                    onSuccess(linkedTenantId);
                } else {
                    toNextStep();
                }

                if (Object.values(groups).length) {
                    Notify.s(
                        t(
                            'Provider.MicrosoftConfiguration.success_group_excluded_added'
                        )
                    );
                }
            } else {
                Notify.e(
                    t('Provider.MicrosoftConfiguration.error_saving_group')
                );
            }
        } catch (error) {
            Notify.e(t('Provider.MicrosoftConfiguration.error_saving_group'));
        } finally {
            setIsSubmitting(false);
        }
    };
    return (
        <>
            {isSubmitting && (
                <Box
                    sx={{
                        alignContent: 'center',
                        display: 'flex',
                        flexWrap: 'wrap',
                        marginRight: '4px'
                    }}
                >
                    <CircularProgress size="32px" />
                </Box>
            )}
            <Button disabled={isSubmitting} onClick={onClick}>
                {t('Common.next')}
            </Button>
        </>
    );
};

export const IntegrationWizard = ({
    filterExistingSyncedTenants,
    integrationType,
    onSuccess,
    setIntegrationType,
    setUseIntegrationSubmitted,
    skipAnimation,
    tenantToEditIntegration
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [linkedTenantId, setLinkedTenantId] = useState(
        tenantToEditIntegration?.id
    );

    const providerRoute =
        integrationType === 'googleAutomated' ? 'google' : 'azure';
    const [skipChooseIntegration, setSkipChooseIntegration] = useState(null);
    const [groups, setGroups] = useState({});

    const params = useParams();

    const clientId = params.clientId || linkedTenantId;
    const providerSteps = [
        t('Onboarding.quickScanChecklist.setupIntegration'),
        t('Onboarding.quickScanChecklist.chooseConfigure'),
        t('Onboarding.quickScanChecklist.confirmGroups'),
        t('Onboarding.quickScanChecklist.searching'),
        t('Onboarding.quickScanChecklist.completed')
    ];

    const tenantToEditIntegrationProviderType =
        tenantToEditIntegration?.providerType;

    useLayoutEffect(() => {
        if (
            tenantToEditIntegrationProviderType === ProviderType.Microsoft ||
            tenantToEditIntegrationProviderType === ProviderType.Google
        ) {
            setSkipChooseIntegration(true);
        }
    }, [tenantToEditIntegrationProviderType]);

    const offset = skipChooseIntegration ? 0 : 1;

    return (
        <Wizard
            align="Vertical"
            allowStepsNav={false}
            key={`integrationWizardAllSteps_${skipChooseIntegration}`}
            initialStep={0}
            showLabel
            stepLabel={t('Common.step')}
            wizardFooterProps={{ display: 'none' }}
        >
            {!skipChooseIntegration ? (
                <WizardPage labelText={providerSteps[0]} step="1">
                    <ChooseIntegration
                        formFooterComponent={
                            <Flex
                                data-testid="wizardFooterChooseIntegration"
                                style={{
                                    ...footerStyles,
                                    ...(tenantToEditIntegration
                                        ? { justifyContent: 'flex-end' }
                                        : {}),
                                    backgroundColor:
                                        theme.palette.background.primaryLight
                                }}
                            >
                                {!tenantToEditIntegration && (
                                    <UnsubmitBackButton
                                        setUseIntegrationSubmitted={
                                            setUseIntegrationSubmitted
                                        }
                                    />
                                )}
                                <FirstButton />
                            </Flex>
                        }
                        integrationType={integrationType}
                        setIntegrationType={setIntegrationType}
                    />
                </WizardPage>
            ) : null}
            <WizardPage
                labelText={providerSteps[1]}
                step={skipChooseIntegration ? '1' : '2'}
            >
                <ConfigureIntegration
                    clientId={clientId}
                    filterExistingSyncedTenants={filterExistingSyncedTenants}
                    formFooterComponent={
                        <Flex
                            data-testid="wizardFooterConfigureIntegration"
                            style={{
                                ...footerStyles,
                                ...(skipChooseIntegration
                                    ? { justifyContent: 'flex-end' }
                                    : {}),
                                backgroundColor:
                                    theme.palette.background.primaryLight
                            }}
                        >
                            {!skipChooseIntegration && <BackButton />}
                            <Flex>
                                {isSubmitting && (
                                    <Box
                                        sx={{
                                            alignContent: 'center',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            marginRight: '4px'
                                        }}
                                    >
                                        <CircularProgress size="32px" />
                                    </Box>
                                )}
                                <Button disabled={isSubmitting} type="submit">
                                    {t('Common.next')}
                                </Button>
                            </Flex>
                        </Flex>
                    }
                    integrationType={integrationType}
                    linkedTenantId={linkedTenantId}
                    setIsSubmitting={setIsSubmitting}
                    setIntegrationType={setIntegrationType}
                    setLinkedTenantId={setLinkedTenantId}
                    tenantToEditIntegration={tenantToEditIntegration}
                />
            </WizardPage>
            <WizardPage labelText={providerSteps[2]} step={`${2 + offset}`}>
                <ChooseGroups
                    groups={groups}
                    integrationType={integrationType}
                    linkedTenantId={linkedTenantId}
                    setGroups={setGroups}
                    formFooterComponent={
                        <Flex
                            data-testid="wizardFooterChooseGroups"
                            style={{
                                ...footerStyles,
                                ...(skipChooseIntegration
                                    ? { justifyContent: 'flex-end' }
                                    : {}),
                                backgroundColor:
                                    theme.palette.background.primaryLight
                            }}
                        >
                            {!skipChooseIntegration && <BackButton />}
                            <Flex>
                                {isSubmitting && (
                                    <Box
                                        sx={{
                                            alignContent: 'center',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            marginRight: '4px'
                                        }}
                                    >
                                        <CircularProgress size="32px" />
                                    </Box>
                                )}
                                <SubmitGroupsButton
                                    skipAnimation={skipAnimation}
                                    onSuccess={onSuccess}
                                    linkedTenantId={linkedTenantId}
                                    groups={groups}
                                    providerRoute={providerRoute}
                                />
                            </Flex>
                        </Flex>
                    }
                />
            </WizardPage>

            {!skipAnimation && (
                <WizardPage
                    labelText={providerSteps[3]}
                    step={`'${3 + offset}'`}
                >
                    <SearchingAppsStep />
                </WizardPage>
            )}

            {!skipAnimation && (
                <WizardPage
                    labelText={providerSteps[4]}
                    step={`'${4 + offset}'`}
                >
                    <Box
                        alignItems="center"
                        display="flex"
                        flexDirection="column"
                        p="32px"
                    >
                        <CheckCircle
                            sx={{
                                color: 'icon.success',
                                fontSize: '128px',
                                marginBottom: '32px',
                                marginTop: '32px'
                            }}
                        />
                        <SuccessfulIntegrationSetup
                            onClick={onSuccess}
                            linkedTenantId={linkedTenantId}
                        />
                    </Box>
                </WizardPage>
            )}
        </Wizard>
    );
};
